import { z } from "zod";
import {
  ChapterAudioSummarySchema,
  ChapterLockSchema,
  FailureSchema,
  IsoDateTimeSchema,
  MoneySchema,
  ProgressSchema,
} from "./common";
import { DraftSummarySchema } from "./drafts";
import {
  AudiobookIdSchema,
  BookIdSchema,
  ChapterIdSchema,
  Sha256Schema,
  VoiceIdSchema,
} from "./ids";
import { JobSummarySchema } from "./jobs";
import { QualityReviewSchema, ReviewPolicySchema } from "./quality";
import {
  BookLanguageSchema,
  BookStateSchema,
  ChapterStateSchema,
  SourceFormatSchema,
  StabilitySchema,
  TtsModelSchema,
} from "./states";

export const SourceInfoSchema = z.strictObject({
  filename: z.string(),
  format: SourceFormatSchema,
  sha256: Sha256Schema,
  sizeBytes: z.int().nonnegative(),
});
export type SourceInfo = z.infer<typeof SourceInfoSchema>;

export const CoverInfoSchema = z.strictObject({
  contentType: z.enum(["image/jpeg", "image/png"]),
  sizeBytes: z.int().nonnegative(),
  updatedAt: IsoDateTimeSchema,
});
export type CoverInfo = z.infer<typeof CoverInfoSchema>;

/**
 * One row of the Chapter rail (CH, STATE, CHARS, EST) plus what the nav and
 * the Book page need per Chapter. Never the script itself: that is ChapterReview.
 */
export const ChapterSummarySchema = z.strictObject({
  id: ChapterIdSchema,
  /** 1-based over every Chapter row, the Book's order (the proposal's "#"). */
  position: z.int().positive(),
  /** 1-based over included Chapters only (the rail's "CH"); null when excluded. */
  number: z.int().positive().nullable(),
  title: z.string(),
  state: ChapterStateSchema,
  excluded: z.boolean(),
  isCalibration: z.boolean(),
  /** Manuscript words and characters in the Chapter's node range. */
  wordCount: z.int().nonnegative(),
  characterCount: z.int().nonnegative(),
  firstLine: z.string().nullable(),
  /** Characters of the current Narration Script; null until the Chapter has one. */
  scriptCharacters: z.int().nonnegative().nullable(),
  /** Money for the current script at the Book's model; null until there is a script. */
  estimate: MoneySchema.nullable(),
  approvedAt: IsoDateTimeSchema.nullable(),
  approvalOrigin: z.enum(["manual", "automatic"]).nullable().optional(),
  audio: ChapterAudioSummarySchema.nullable(),
  lock: ChapterLockSchema.nullable(),
  /** Refinement or render progress while a job runs on this Chapter. */
  progress: ProgressSchema.nullable(),
  failure: FailureSchema.nullable(),
  /** Number of script versions; 0 before the first Refinement. */
  versionCount: z.int().nonnegative(),
});
export type ChapterSummary = z.infer<typeof ChapterSummarySchema>;

export const BookCountsSchema = z.strictObject({
  chapters: z.int().nonnegative(),
  included: z.int().nonnegative(),
  excluded: z.int().nonnegative(),
  refining: z.int().nonnegative(),
  inReview: z.int().nonnegative(),
  approved: z.int().nonnegative(),
  rendering: z.int().nonnegative(),
  rendered: z.int().nonnegative(),
  failed: z.int().nonnegative(),
});
export type BookCounts = z.infer<typeof BookCountsSchema>;

export const StyleSheetSummarySchema = z.strictObject({
  version: z.int().positive(),
  termCount: z.int().nonnegative(),
  instructionCount: z.int().nonnegative(),
  /** Chapters the "re-run unapproved" offer would touch. */
  unapprovedChapterCount: z.int().nonnegative(),
  updatedAt: IsoDateTimeSchema,
});
export type StyleSheetSummary = z.infer<typeof StyleSheetSummarySchema>;

export const CastingSummarySchema = z.strictObject({
  narrator: z.strictObject({ voiceId: VoiceIdSchema, name: z.string() }).nullable(),
  /** Locked once any Chapter has Chapter Audio; model and stability lock at the same moment. */
  locked: z.boolean(),
});
export type CastingSummary = z.infer<typeof CastingSummarySchema>;

/** The Book total the rail shows ("book estimate ≈ $31"): the Chapters still to render. */
export const EstimateSummarySchema = z.strictObject({
  chapterCount: z.int().nonnegative(),
  characters: z.int().nonnegative(),
  credits: z.int().nonnegative(),
  money: MoneySchema,
});
export type EstimateSummary = z.infer<typeof EstimateSummarySchema>;

export const RENDER_STATUSES = [
  "idle",
  "rendering",
  "stopping",
  "assembling",
  "rendered",
  "failed",
  "stopped",
] as const;
export const RenderStatusSchema = z.enum(RENDER_STATUSES);
export type RenderStatus = z.infer<typeof RenderStatusSchema>;

export const RenderSummarySchema = z.strictObject({
  status: RenderStatusSchema,
  chaptersDone: z.int().nonnegative(),
  chaptersTotal: z.int().nonnegative(),
  etaSeconds: z.int().nonnegative().nullable(),
  spent: MoneySchema,
});
export type RenderSummary = z.infer<typeof RenderSummarySchema>;

export const AudiobookSummarySchema = z.strictObject({
  id: AudiobookIdSchema,
  stale: z.boolean(),
  durationMs: z.int().nonnegative(),
  createdAt: IsoDateTimeSchema,
});
export type AudiobookSummary = z.infer<typeof AudiobookSummarySchema>;

/**
 * GET /books/:id, the poll target (every 2 s while work is active). Everything
 * the nav, the Book page, and the Chapter rail render comes from here; the
 * per-screen payloads (proposal, style sheet, review, casting, estimate,
 * render progress, audiobook) have their own routes.
 */
export const BookDetailSchema = z.strictObject({
  drafts: z.array(DraftSummarySchema).optional(),
  metadataConfirmedAt: IsoDateTimeSchema.nullable().optional(),
  metadataExtraction: z.record(z.string(), z.unknown()).nullable().optional(),
  nextAction: z.strictObject({ key: z.string(), path: z.string().nullable() }).optional(),
  reviewPolicy: ReviewPolicySchema.optional(),
  qualityReviews: z.array(QualityReviewSchema).optional(),
  id: BookIdSchema,
  state: BookStateSchema,
  title: z.string().nullable(),
  authorName: z.string().nullable(),
  /** The Book's language: detected by Parse, correctable until confirmation. */
  language: BookLanguageSchema.nullable(),
  languageDetected: BookLanguageSchema.nullable(),
  source: SourceInfoSchema,
  cover: CoverInfoSchema.nullable(),
  ttsModel: TtsModelSchema,
  stability: StabilitySchema,
  /** Model and stability are frozen once the first Chapter Audio exists. */
  renderSettingsLockedAt: IsoDateTimeSchema.nullable(),
  calibrationChapterId: ChapterIdSchema.nullable(),
  confirmedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /** Set while state is failed: Parse, Chapter Proposal, or Style Sheet failed after the automatic retries. */
  failure: FailureSchema.nullable(),
  /** The job the retry button targets when state is failed. */
  retryJobId: JobSummarySchema.shape.id.nullable(),
  /** Queued and running jobs plus jobs parked for manual retry. */
  activeJobs: z.array(JobSummarySchema),
  chapters: z.array(ChapterSummarySchema),
  counts: BookCountsSchema,
  styleSheet: StyleSheetSummarySchema.nullable(),
  casting: CastingSummarySchema,
  estimate: EstimateSummarySchema.nullable(),
  render: RenderSummarySchema.nullable(),
  audiobook: AudiobookSummarySchema.nullable(),
});
export type BookDetail = z.infer<typeof BookDetailSchema>;

/** One row of the dashboard's book list. */
export const BookSummarySchema = z.strictObject({
  id: BookIdSchema,
  title: z.string().nullable(),
  authorName: z.string().nullable(),
  language: BookLanguageSchema.nullable(),
  state: BookStateSchema,
  /** Included Chapters. */
  chapterCount: z.int().nonnegative(),
  approvedCount: z.int().nonnegative(),
  renderedCount: z.int().nonnegative(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  renderedAt: IsoDateTimeSchema.nullable(),
  failure: FailureSchema.nullable(),
});
export type BookSummary = z.infer<typeof BookSummarySchema>;

export const BookListResponseSchema = z.strictObject({ books: z.array(BookSummarySchema) });
export type BookListResponse = z.infer<typeof BookListResponseSchema>;

/**
 * PATCH /books/:id. Title and author at any time; language until confirmed
 * (409 BOOK_STATE_CONFLICT); model and stability until the first Chapter
 * Audio (409 RENDER_SETTINGS_LOCKED).
 */
export const BookPatchSchema = z
  .strictObject({
    title: z.string().trim().min(1).max(300).optional(),
    authorName: z.string().trim().max(4000).nullable().optional(),
    language: BookLanguageSchema.optional(),
    ttsModel: TtsModelSchema.optional(),
    stability: StabilitySchema.optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: "empty patch" });
export type BookPatch = z.infer<typeof BookPatchSchema>;

export const BookDeleteResponseSchema = z.strictObject({
  id: BookIdSchema,
  deleted: z.literal(true),
});
export type BookDeleteResponse = z.infer<typeof BookDeleteResponseSchema>;
